import * as React from 'react';
import { t } from '@lingui/core/macro';

export interface IConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  'data-testid'?: string;
  /**
   * Optional custom content to render below the message.
   * Use this to add form fields, additional information, or any custom UI.
   */
  children?: React.ReactNode;
}

/**
 * A reusable confirmation dialog component that replaces window.confirm.
 * Provides a consistent modal interface for user confirmation actions.
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = t`Confirm`,
  cancelText = t`Cancel`,
  onConfirm,
  onCancel,
  'data-testid': dataTestId,
  children = null,
}: IConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = React.useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  // Handle Escape key
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="relative z-[101]"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      data-testid={dataTestId}
    >
      <div className="fixed inset-0 bg-teal-80020 transition-opacity" aria-hidden="true"></div>
      <div className="fixed inset-0 z-[102] w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div>
              <div className="mt-3 sm:mt-2">
                <h2 className="text-base font-semibold text-gray-900" id="modal-title">
                  {title}
                </h2>
                {message && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-700">{message}</p>
                  </div>
                )}
                {children && <div className="mt-4">{children}</div>}
              </div>
            </div>
            <div className="mt-5 sm:mt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="hover:bg-gray-50 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
                >
                  {cancelText}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="inline-flex w-full justify-center rounded-md bg-teal px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
